import { FC, KeyboardEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { TrackingApi, UserDictionaryApi } from 'src/api';
import { Button, GrowthLevelIndicator, Icon, IconToggle, Tooltip } from 'src/components';
import { LikeVideoButton } from 'src/components/LikeVideoButton';
import theme from 'src/utils/theme';
import { useMediaQuery } from 'react-responsive';
import { hasTriggeredExtraClickFromKeyUpEvent } from 'src/apps/LearningSession/utils/hasTriggeredExtraClickFromKeyUpEvent';
import { useExperienceContext } from 'src/context/experience';
import { useUserContext } from 'src/context/user';
import { getActiveWordlistId } from 'src/utils/wordlists/getActiveWordlistId';
import { ToggleDifficultAction, ToggleIgnoredAction } from '../../hooks/learningSessionReducer';
import * as S from './styles';

const msgs = defineMessages({
  markDifficult: {
    id: 'learning_session_tooltip_markdifficult',
  },
  ignoreItem: {
    id: 'learning_session_tooltip_ignoreitem_futuresessions',
  },
  iakt: { id: 'scenario_lesson_i_already_know_this' },
  replayVideo: { id: 'learning_session_keyboard_shortcuts_replay_media' },
});

type LearnableMutation = (languagePairId: number, learnableId: number) => void;

export type GrowthLevelAndTogglesProps = {
  growthLevel: number;
  languagePairId: number;
  learnableId: number;
  ignored: boolean;
  isItemDueForReview: boolean;
  difficult: boolean;
  learningSessionDispatch: (action: ToggleDifficultAction | ToggleIgnoredAction) => void;
  onItemIgnored: () => void;
  onItemDifficultyChanged: (difficult: boolean) => void;
  markAsDifficult?: LearnableMutation;
  markAsNotDifficult?: LearnableMutation;
  ignore?: LearnableMutation;
  unignore?: LearnableMutation;
  onlyDifficultIcon?: boolean;
  onlyIAKTButton?: boolean;
  /** Circular icon control for embedding IAKT in the video controls row. */
  onlyIAKTIcon?: boolean;
  onReplayVideo?: () => void;
  videoLiked?: boolean;
  onToggleLikeVideo?: () => void;
  videoSrc?: string | null;
};

const defaultMarkAsDifficult: LearnableMutation = (languagePairId, learnableId) =>
  UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
    difficult: true,
  });
const defaultMarkAsNotDifficult: LearnableMutation = (languagePairId, learnableId) =>
  UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
    difficult: false,
  });
const defaultIgnore: LearnableMutation = (languagePairId, learnableId) =>
  UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
    known: true,
  });
const defaultUnignore: LearnableMutation = (languagePairId, learnableId) =>
  UserDictionaryApi.updateSingleLearnable(languagePairId, learnableId, {
    known: false,
  });

// Time the toggles remain disabled to prevent users from clicking by mistake
export const togglesDisabledDelay = 500; // ms

export const GrowthLevelAndToggles: FC<GrowthLevelAndTogglesProps> = ({
  growthLevel,
  languagePairId,
  learnableId,
  ignored,
  isItemDueForReview,
  difficult,
  learningSessionDispatch,
  onItemIgnored,
  onItemDifficultyChanged,
  markAsDifficult = defaultMarkAsDifficult,
  markAsNotDifficult = defaultMarkAsNotDifficult,
  ignore = defaultIgnore,
  unignore = defaultUnignore,
  onlyDifficultIcon,
  onlyIAKTButton,
  onlyIAKTIcon,
  onReplayVideo,
  videoLiked = false,
  onToggleLikeVideo,
  videoSrc,
  ...props
}) => {
  const intl = useIntl();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  const { experienceType } = useExperienceContext();
  const { user } = useUserContext();
  const [isDisabledIgnore, setIsDisabledIgnore] = useState(true);
  const [isDisabledDifficult, setIsDisabledDifficult] = useState(true);

  useEffect(() => {
    setIsDisabledIgnore(false);
    setIsDisabledDifficult(true);

    const timeoutId = setTimeout(() => {
      setIsDisabledDifficult(false);
    }, togglesDisabledDelay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [learnableId]);

  const handleIgnoreToggle = useCallback(async () => {
    if (!ignored) {
      const isProUser = !!user?.isPro;
      const wordlistId = getActiveWordlistId();
      void TrackingApi.trackUserAction(
        'clicked_i_already_know_this',
        'learning_session',
        languagePairId,
        isProUser,
        {
          is_pro_user: String(isProUser),
          ...(wordlistId && { wordlist_id: wordlistId }),
        },
      );
    }

    learningSessionDispatch({
      type: 'TOGGLE_IGNORED',
      learnableId,
    });
    setIsDisabledIgnore(true);

    try {
      if (ignored) {
        unignore(languagePairId, learnableId);
      } else {
        /* ignore needs to be awaited to prevent a race condition with the learning_sessions/end request */
        await ignore(languagePairId, learnableId);
        onItemIgnored();
      }
    } catch (error) {
      /* istanbul ignore next: unexpected error */
      console.error({ error });
    }
  }, [
    ignore,
    ignored,
    languagePairId,
    learnableId,
    learningSessionDispatch,
    onItemIgnored,
    unignore,
    user?.isPro,
  ]);

  const handleDifficultWordToggle = () => {
    try {
      if (difficult) {
        markAsNotDifficult(languagePairId, learnableId);
      } else {
        markAsDifficult(languagePairId, learnableId);
      }
    } catch (error) {
      /* istanbul ignore next: unexpected error */
      console.error({ error });
    }

    learningSessionDispatch({
      type: 'TOGGLE_DIFFICULT',
      learnableId,
    });

    onItemDifficultyChanged(!difficult);
  };

  const onClick = async (event?: MouseEvent) => {
    if (hasTriggeredExtraClickFromKeyUpEvent(event)) return;
    await handleIgnoreToggle();
  };

  const handleKeyUp = useCallback(
    async (event?: KeyboardEvent) => {
      if (event?.key === 'Enter' || event?.key === ' ') {
        event.stopPropagation();
        await handleIgnoreToggle();
      }
    },
    [handleIgnoreToggle],
  );

  if (onlyIAKTIcon) {
    return (
      <Tooltip placement="bottom" tooltip={intl.formatMessage(msgs.iakt)} disabled={isMobile}>
        <S.DifficultWordIconWrapper
          type="button"
          onClick={onClick}
          onKeyUp={handleKeyUp}
          $active={ignored}
          disabled={isDisabledIgnore}
          aria-label={intl.formatMessage(msgs.iakt)}
          data-testid="iakt-icon"
        >
          <Icon name={ignored ? 'tickFilled' : 'tickOutline'} />
        </S.DifficultWordIconWrapper>
      </Tooltip>
    );
  }

  if (onlyIAKTButton)
    return (
      <Button
        icon="tickOutline"
        buttonType="secondary"
        buttonSize="medium"
        onClick={onClick}
        onKeyUp={handleKeyUp}
        disabled={isDisabledIgnore}
        wide
      >
        {intl.formatMessage(msgs.iakt)}
      </Button>
    );

  return onlyDifficultIcon ? (
    <Tooltip
      placement="bottom"
      tooltip={intl.formatMessage(msgs.markDifficult)}
      disabled={isMobile}
    >
      <S.DifficultWordIconWrapper
        type="button"
        onClick={handleDifficultWordToggle}
        $active={difficult}
        aria-label={intl.formatMessage(msgs.markDifficult)}
        data-testid="difficult-word-icon"
      >
        <Icon name={difficult ? 'difficultWordsFilled' : 'difficultWordsOutline'} />
      </S.DifficultWordIconWrapper>
    </Tooltip>
  ) : (
    <S.GrowthLevelWrapper {...props}>
      <S.ToggleWrapper
        $withReplay={!!onReplayVideo}
        $withLike={!!onReplayVideo && !!onToggleLikeVideo}
        data-testid={onReplayVideo ? 'test-video-toggle-overlay' : undefined}
      >
        {onReplayVideo && (
          <Tooltip
            placement="bottom"
            tooltip={intl.formatMessage(msgs.replayVideo)}
            disabled={isMobile}
          >
            <S.DifficultWordIconWrapper
              type="button"
              onClick={onReplayVideo}
              aria-label={intl.formatMessage(msgs.replayVideo)}
              data-testid="test-replay-video"
            >
              <Icon name="replayThin" />
            </S.DifficultWordIconWrapper>
          </Tooltip>
        )}
        <Tooltip
          placement="bottom"
          tooltip={intl.formatMessage(msgs.markDifficult)}
          disabled={isMobile}
        >
          <IconToggle
            iconName={difficult ? 'difficultWordsFilled' : 'difficultWordsOutline'}
            handleClick={handleDifficultWordToggle}
            isSelected={difficult}
            disabled={isDisabledDifficult}
          />
        </Tooltip>

        <Tooltip
          placement="bottom"
          tooltip={intl.formatMessage(msgs.ignoreItem)}
          disabled={isMobile}
        >
          <IconToggle
            iconName={ignored ? 'tickFilled' : 'tickOutline'}
            handleClick={onClick}
            handleKeyUp={handleKeyUp}
            isSelected={ignored}
            disabled={isDisabledIgnore}
          />
        </Tooltip>
        {onReplayVideo && onToggleLikeVideo && (
          <LikeVideoButton
            liked={videoLiked}
            onToggle={onToggleLikeVideo}
            languagePairId={languagePairId}
            learnableId={learnableId}
            videoSrc={videoSrc}
          />
        )}
      </S.ToggleWrapper>
      {experienceType === '2024' && (
        <GrowthLevelIndicator growthLevel={growthLevel} isItemDueForReview={isItemDueForReview} />
      )}
    </S.GrowthLevelWrapper>
  );
};
